import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { RecordDetailSection } from '@netfoundry-ui/ui/detail-drawer';
import { forgetFixedColumnWidths, LocalListTable, oidcListTabs } from '@netfoundry-ui/ui/table';
import { Subscription } from 'rxjs';
import { DataTableFilterService, ListColumn, ListSortDir } from 'ziti-console-lib';
import { OidcIdentityAudienceMapComponent } from './oidc-identity-audience-map.component';

const TABLE_ID = 'oidc-identity-audience-maps-list';
const SEARCH_COLUMN = 'id';
const DEFAULT_PAGE_SIZE = 30;

export type AudienceMapRow = {
    id: string;
    userIdentityId?: string;
    oidcAudienceId?: string;
    subject?: string;
    active?: boolean;
    createdAt?: string;
    createdBy?: string;
    selected?: boolean;
};

function formatDate(value: string | undefined): string {
    const date = value ? new Date(value) : undefined;
    return date && !isNaN(date.getTime()) ? date.toLocaleString() : value || '';
}

/** Sorts rows by one column's text, case-insensitive. No sort keeps the order the API gave. */
export function sortAudienceMapRows(
    rows: AudienceMapRow[],
    sort: { key: string; dir: ListSortDir } | undefined
): AudienceMapRow[] {
    if (!sort) {
        return rows;
    }
    const value = (row: AudienceMapRow) => String(row[sort.key] ?? '').toLowerCase();
    return [...rows].sort((a, b) => (sort.dir === 'desc' ? -1 : 1) * value(a).localeCompare(value(b)));
}

@Component({
    selector: 'app-oidc-identity-audience-map-list',
    templateUrl: './oidc-identity-audience-map-list.component.html',
    styleUrls: ['./oidc-identity-audience-map-list.component.scss'],
    standalone: false,
})
export class OidcIdentityAudienceMapListComponent
    extends OidcIdentityAudienceMapComponent
    implements OnInit, OnDestroy
{
    readonly tableId = TABLE_ID;
    readonly searchFilter = SEARCH_COLUMN;
    readonly initialPageSize = DEFAULT_PAGE_SIZE;
    readonly tableOptions = { noSelect: true, pager: 'pages' as const };
    readonly menuItems = [];

    readonly tabs = oidcListTabs();

    listColumns: ListColumn<AudienceMapRow>[] = [];
    readonly list = new LocalListTable<AudienceMapRow>(
        inject(DataTableFilterService),
        TABLE_ID,
        DEFAULT_PAGE_SIZE,
        SEARCH_COLUMN,
        () => this.refresh()
    );

    private readonly listSub = new Subscription();
    private sortState: { key: string; dir: ListSortDir } | undefined;

    get accessDenied(): boolean {
        return !this.authorizationService.canListOIDCAudience();
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.listColumns = this.buildColumns();
        forgetFixedColumnWidths(TABLE_ID, this.listColumns);
        this.listSub.add(this.list.connect());
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.listSub.unsubscribe();
    }

    // The legacy load ends here every time the list is read, so the rows go to the table instead of the old grid.
    override shouldShowNoData(): void {
        super.shouldShowNoData();
        this.list.setItems(sortAudienceMapRows(this.oidcIdentityAudienceMaps as AudienceMapRow[], this.sortState));
    }

    detailSections(row: AudienceMapRow): RecordDetailSection[] {
        return [
            {
                title: 'Mapping',
                fields: [
                    { label: 'Subject', value: row.subject, mono: true },
                    { label: 'User Identity Id', value: row.userIdentityId, mono: true },
                    { label: 'Audience Id', value: row.oidcAudienceId, mono: true },
                    { label: 'Status', value: row.active ? 'Active' : 'Inactive' },
                ],
            },
            {
                title: 'Record',
                fields: [
                    { label: 'Id', value: row.id, mono: true },
                    { label: 'Created', value: formatDate(row.createdAt) },
                    { label: 'Created By', value: row.createdBy, mono: true },
                ],
            },
        ];
    }

    private buildColumns(): ListColumn<AudienceMapRow>[] {
        const open = (ctx: { row: AudienceMapRow }) => this.list.toggle(ctx.row);
        const onSort = (key: string, dir: ListSortDir) => {
            this.sortState = { key, dir };
            this.list.setItems(sortAudienceMapRows(this.oidcIdentityAudienceMaps as AudienceMapRow[], this.sortState));
        };
        const text = (key: keyof AudienceMapRow): ListColumn<AudienceMapRow>['valueFormatter'] => (shim) =>
            String(shim.data[key] ?? '');
        return [
            { key: 'id', label: 'Id', minWidth: 280, sortable: true, onSort, valueFormatter: text('id'), onCellClick: open },
            {
                key: 'userIdentityId',
                label: 'User Identity Id',
                minWidth: 280,
                sortable: true,
                onSort,
                valueFormatter: text('userIdentityId'),
                onCellClick: open,
            },
            {
                key: 'oidcAudienceId',
                label: 'Audience Id',
                minWidth: 280,
                sortable: true,
                onSort,
                valueFormatter: text('oidcAudienceId'),
                onCellClick: open,
            },
            { key: 'subject', label: 'Subject', minWidth: 200, sortable: true, onSort, valueFormatter: text('subject'), onCellClick: open },
            {
                key: 'active',
                label: 'Active',
                width: 120,
                sortable: true,
                onSort,
                cellHtml: (shim) =>
                    `<span class="lt-status lt-status-${shim.data.active ? 'online' : 'offline'}">${
                        shim.data.active ? 'Active' : 'Inactive'
                    }</span>`,
                onCellClick: open,
            },
        ];
    }
}
